/**
 * Single source of truth for the consolidated Shopify trigger node.
 *
 * The palette used to carry TEN separate Shopify trigger nodes (Order Created,
 * Order Updated, Order Paid, …). They are now collapsed into ONE palette entry
 * — `shopify_event_trigger` — whose event is chosen from a dropdown in the Node
 * Configuration panel.
 *
 * ## This is a frontend-only consolidation
 *
 * The backend dispatches flows on the `automation_flows.trigger_type` COLUMN
 * (see `TriggerDispatcher.js`, which queries for `order.paid`, `refund.created`
 * and friends). That column is derived at save time by `extractTriggerInfo()`
 * in FlowBuilder from the starting node. So all this node has to do is report
 * the SAME `trigger_type` string the ten individual nodes reported, and the
 * backend is none the wiser — no API, schema, or dispatcher change.
 *
 * `legacyNodeType` records which old node each event replaces. The ten legacy
 * types remain registered on the canvas (`nodeTypes` in FlowBuilder) so saved
 * flows built with them keep loading and running untouched; they are simply no
 * longer offered in the palette.
 *
 * ⚠️  `triggerType` values are a WIRE CONTRACT with the backend dispatcher.
 * Changing one silently stops matching flows from firing. If you add an event,
 * confirm the backend actually dispatches that string first.
 */

/**
 * One selectable `{{node.path}}` a downstream node can read off this trigger.
 * `path` is relative to the trigger node's id, so the picker renders
 * `{{shopify_event_trigger_1234.orderDetails.name}}`.
 */
export interface ShopifyTriggerVariableField {
  /** Dot-path appended to the node id. Empty string = the node id itself. */
  path: string;
  /** Suffix shown after the node label in the variable picker. */
  label: string;
}

export interface ShopifyTriggerEvent {
  /** Value stored in `node.data.shopifyEvent`. */
  id: string;
  /** Dropdown label. */
  label: string;
  /** One-line explanation shown under the dropdown. */
  description: string;
  /**
   * The `trigger_type` written to `automation_flows` on save. MUST match what
   * `TriggerDispatcher` queries for this event.
   */
  triggerType: string;
  /** The pre-consolidation node type this event replaces. */
  legacyNodeType: string;
  /**
   * The variables this event puts on `trigger_data`, surfaced to every
   * downstream node's variable picker.
   *
   * ⚠️  These paths mirror what the BACKEND actually dispatches — see
   * `ShopifyConnectorService` (the `dispatchTrigger` payloads) and
   * `TriggerDispatcher`. Each event carries a DIFFERENT envelope
   * (`orderDetails` / `checkoutDetails` / `refundDetails` / `customerDetails`
   * / `fulfillmentDetails`), which is exactly why the consolidated node cannot
   * offer one flat list for all ten. Advertising a path the backend never
   * sends renders an empty string at runtime with no error, so confirm against
   * the dispatch site before adding one.
   */
  variableFields: ShopifyTriggerVariableField[];
}

/**
 * Order + customer fields common to every order-shaped event. The backend
 * sends the raw Shopify order as `orderDetails` on all of them.
 */
const ORDER_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.name', label: 'Order Name' },
  { path: 'orderDetails.id', label: 'Order ID' },
  { path: 'orderDetails.total_price', label: 'Order Price' },
  { path: 'orderDetails.currency', label: 'Order Currency' },
  { path: 'orderDetails.status', label: 'Order Status' },
  { path: 'orderDetails.financial_status', label: 'Payment Status' },
  { path: 'orderDetails.fulfillment_status', label: 'Fulfillment Status' },
  { path: 'orderDetails.note', label: 'Order Note' },
  { path: 'orderDetails.order_status_url', label: 'Order Link' },
];

const ORDER_CUSTOMER_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.customer.first_name', label: 'Customer First Name' },
  { path: 'orderDetails.customer.last_name', label: 'Customer Last Name' },
  { path: 'orderDetails.email', label: 'Customer Email' },
  { path: 'orderDetails.customer.phone', label: 'Customer Phone' },
];

/** Trimmed order set used by the fulfillment + refund events. */
const ORDER_SUMMARY_FIELDS: ShopifyTriggerVariableField[] = [
  { path: 'orderDetails.name', label: 'Order Name' },
  { path: 'orderDetails.total_price', label: 'Order Price' },
];

/** `{{node}}` on its own renders the whole formatted payload. */
const BASE_FIELD: ShopifyTriggerVariableField = { path: '', label: '(Formatted)' };

const ORDER_EVENT_FIELDS: ShopifyTriggerVariableField[] = [
  BASE_FIELD,
  ...ORDER_FIELDS,
  ...ORDER_CUSTOMER_FIELDS,
];

/** Shipped / out-for-delivery / delivered all carry `fulfillmentDetails`. */
const FULFILLMENT_EVENT_FIELDS: ShopifyTriggerVariableField[] = [
  BASE_FIELD,
  { path: 'fulfillmentDetails.tracking_company', label: 'Tracking Company' },
  { path: 'fulfillmentDetails.tracking_number', label: 'Tracking Number' },
  { path: 'fulfillmentDetails.tracking_url', label: 'Tracking URL' },
  { path: 'fulfillmentDetails.status', label: 'Fulfillment Status' },
  ...ORDER_SUMMARY_FIELDS,
  ...ORDER_CUSTOMER_FIELDS,
];

export const SHOPIFY_TRIGGER_EVENTS: ShopifyTriggerEvent[] = [
  {
    id: 'order_created',
    label: 'Order Created',
    description: 'Runs when a new order is placed in your connected store.',
    triggerType: 'order.placed',
    legacyNodeType: 'shopify_order_created_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_updated',
    label: 'Order Updated',
    description: 'Runs when an existing order is updated.',
    triggerType: 'order.updated',
    legacyNodeType: 'shopify_order_updated_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_paid',
    label: 'Order Paid',
    description: 'Runs when payment for an order is captured.',
    triggerType: 'order.paid',
    legacyNodeType: 'shopify_order_paid_trigger',
    variableFields: ORDER_EVENT_FIELDS,
  },
  {
    id: 'order_shipped',
    label: 'Order Shipped',
    description: 'Runs when an order ships and is in transit.',
    triggerType: 'order.shipped',
    legacyNodeType: 'shopify_order_shipped_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_out_for_delivery',
    label: 'Out For Delivery',
    description: 'Runs when an order is out for delivery.',
    triggerType: 'order.out_for_delivery',
    legacyNodeType: 'shopify_order_out_for_delivery_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_delivered',
    label: 'Order Delivered',
    description: 'Runs when an order is marked delivered.',
    triggerType: 'order.delivered',
    legacyNodeType: 'shopify_order_delivered_trigger',
    variableFields: FULFILLMENT_EVENT_FIELDS,
  },
  {
    id: 'order_cancelled',
    label: 'Order Cancelled',
    description: 'Runs when an order is cancelled.',
    triggerType: 'order.cancelled',
    legacyNodeType: 'shopify_order_cancelled_trigger',
    // Same order envelope, plus the cancellation reason the legacy
    // `shopify_order_cancelled_trigger` branch exposed.
    variableFields: [
      ...ORDER_EVENT_FIELDS,
      { path: 'cancelReason', label: 'Cancel Reason' },
    ],
  },
  {
    id: 'refund_created',
    label: 'Refund Created',
    description: 'Runs when a refund is created for an order.',
    triggerType: 'refund.created',
    legacyNodeType: 'shopify_refund_created_trigger',
    // Backend sends BOTH `refundDetails` and `orderDetails` here.
    variableFields: [
      BASE_FIELD,
      { path: 'refundDetails.order_id', label: 'Order ID' },
      { path: 'refundDetails.note', label: 'Refund Note' },
      ...ORDER_SUMMARY_FIELDS,
      ...ORDER_CUSTOMER_FIELDS,
    ],
  },
  {
    id: 'abandoned_cart',
    label: 'Abandoned Cart',
    description: 'Runs when a checkout is abandoned or updated.',
    triggerType: 'checkout.updated',
    legacyNodeType: 'shopify_abandoned_cart_trigger',
    // A checkout is NOT an order — the payload is `checkoutDetails`, so none
    // of the `orderDetails.*` paths resolve on this event.
    variableFields: [
      BASE_FIELD,
      { path: 'checkoutDetails.abandoned_checkout_url', label: 'Recovery Link' },
      { path: 'checkoutDetails.total_price', label: 'Cart Total' },
      { path: 'checkoutDetails.customer.first_name', label: 'Customer First Name' },
      { path: 'checkoutDetails.customer.last_name', label: 'Customer Last Name' },
      { path: 'checkoutDetails.email', label: 'Customer Email' },
      { path: 'checkoutDetails.customer.phone', label: 'Customer Phone' },
    ],
  },
  {
    id: 'customer_created',
    label: 'New Subscriber',
    description: 'Runs when a new customer is created in your store.',
    triggerType: 'customer.created',
    legacyNodeType: 'shopify_customer_created_trigger',
    // No order exists yet — only `customerDetails` is dispatched.
    variableFields: [
      BASE_FIELD,
      { path: 'customerDetails.first_name', label: 'Customer First Name' },
      { path: 'customerDetails.last_name', label: 'Customer Last Name' },
      { path: 'customerDetails.email', label: 'Customer Email' },
      { path: 'customerDetails.phone', label: 'Customer Phone' },
    ],
  },
];

/** The consolidated palette/canvas node type. */
export const SHOPIFY_EVENT_TRIGGER_TYPE = 'shopify_event_trigger';

/**
 * Event selected when a freshly-dropped node has no choice stored yet.
 * Order Created is the most common Shopify automation entry point.
 */
export const DEFAULT_SHOPIFY_EVENT_ID = 'order_created';

/** Look up an event by its stored id, falling back to the default. */
export function getShopifyEvent(eventId?: string | null): ShopifyTriggerEvent {
  const found = SHOPIFY_TRIGGER_EVENTS.find((e) => e.id === eventId);
  return found ?? SHOPIFY_TRIGGER_EVENTS.find((e) => e.id === DEFAULT_SHOPIFY_EVENT_ID)!;
}

/**
 * Resolve the `trigger_type` for a node's stored event. Used by
 * `extractTriggerInfo()` so the saved flow keeps the exact wire value the
 * backend dispatcher expects.
 */
export function shopifyTriggerTypeFor(eventId?: string | null): string {
  return getShopifyEvent(eventId).triggerType;
}

/**
 * Map a LEGACY shopify node type back to its event id, so a saved flow built
 * with one of the ten old nodes can be shown in the new dropdown if it is ever
 * opened through the consolidated node.
 */
export function eventIdForLegacyNodeType(nodeType: string): string | null {
  return SHOPIFY_TRIGGER_EVENTS.find((e) => e.legacyNodeType === nodeType)?.id ?? null;
}

/**
 * Read the event id off a canvas node's `data`, tolerating both the flat and
 * `config`-nested shapes the inspector writes.
 */
export function shopifyEventIdFromNodeData(
  data?: Record<string, unknown> | null,
): string | undefined {
  const nested = data?.config as Record<string, unknown> | undefined;
  return (data?.shopifyEvent ?? nested?.shopifyEvent) as string | undefined;
}

/**
 * The variable fields a downstream node can read off this trigger.
 *
 * Accepts EITHER the consolidated node's stored event id OR a legacy node type
 * (`shopify_order_paid_trigger`), so a flow saved before the consolidation
 * resolves to the same list. Unknown input falls back to the default event's
 * fields rather than returning nothing — an empty picker is the bug this
 * function exists to prevent.
 */
export function shopifyVariableFieldsFor(
  eventIdOrLegacyType?: string | null,
): ShopifyTriggerVariableField[] {
  const legacyMatched = eventIdOrLegacyType
    ? eventIdForLegacyNodeType(eventIdOrLegacyType)
    : null;
  return getShopifyEvent(legacyMatched ?? eventIdOrLegacyType).variableFields;
}

/** Every legacy type this node consolidates — kept registered for old flows. */
export const LEGACY_SHOPIFY_NODE_TYPES = SHOPIFY_TRIGGER_EVENTS.map(
  (e) => e.legacyNodeType,
);
